import { useMutation, useQuery } from "@tanstack/react-query";
import { Select } from "antd";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { apiFetch } from "../api/client";
import type { ListResponse, NotificationsResponse, PublicLanguageRow } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { SUPPORTED_LOCALES, currentLocale, isSupportedLocale, setLocale } from "../i18n";
import type { SupportedLocale } from "../i18n";

const LABELS: Record<SupportedLocale, string> = {
  de: "Deutsch",
  en: "English",
  it: "Italiano",
};

/**
 * The one place the interface language is decided — as a hook, because it is
 * offered in two places: the picker on the public pages and the user menu
 * inside the application.
 *
 * It is an account setting, not a browser setting. The server has always
 * treated it that way — ApplicationController#set_language resolves the
 * logged-in user's preference first — while the client used to pick its own
 * from localStorage and, failing that, from `navigator.languages`. The two
 * therefore drifted apart in a way that was easy to misread: everything coming
 * from the API arrived in the account's language, while everything the bundle
 * renders itself, antd's own texts included, followed the browser. An English
 * browser with a German account produced German menus and English date pickers.
 *
 * So both directions are wired here: the account's language wins on login and
 * on every reload, and picking one stores it on the account through
 * main#change_language — the same endpoint the old menu used.
 *
 * Which languages are on offer is the site's decision: welcome#languages lists
 * the ones that are both active and marked as system languages, the same list
 * the registration form fills its picker from.
 */
export function useLanguageSwitch() {
  const { status, user, setUserLanguage } = useAuth();

  const languages = useQuery({
    queryKey: ["welcome", "languages"],
    // Public, and needed on the login screen as well — no token involved.
    queryFn: () =>
      apiFetch<ListResponse<PublicLanguageRow>>("/welcome/languages", { anonymous: true }),
    // The site's languages change about never; re-asking on every screen the
    // switcher appears on would be noise.
    staleTime: 60 * 60 * 1000,
  });

  const rows = languages.data?.data;

  const store = useMutation({
    mutationFn: (languageId: number) =>
      apiFetch<NotificationsResponse>(`/main/language/${languageId}`, { method: "PUT" }),
  });

  const selected = currentLocale();

  // Adopting the account language must not fight the user: once they have
  // picked something here, the choice is stored on the account and this is
  // what stops the effect from reading the previous value back out of a
  // response that is still in flight.
  const adoptedFor = useRef<number | null>(null);

  useEffect(() => {
    if (status !== "authenticated" || !user || !rows) return;
    if (adoptedFor.current === user.id) return;

    adoptedFor.current = user.id;

    const code = rows.find((row) => row.id === user.language_id)?.language_code?.toLowerCase();
    // A language the bundle has no strings for — Portuguese, say — leaves the
    // client where it is rather than falling back to a half-translated screen.
    if (isSupportedLocale(code) && code !== currentLocale()) setLocale(code);
  }, [status, user, rows]);

  const change = (locale: SupportedLocale) => {
    // The account first: changing the language remounts the tree below the
    // ConfigProvider, this component with it, and the effect above reads the
    // cached user again on the way back.
    if (status === "authenticated") {
      const row = rows?.find((item) => item.language_code?.toLowerCase() === locale);
      if (row) {
        setUserLanguage(row.id);
        store.mutate(row.id);
      }
    }

    setLocale(locale);
  };

  const active = rows
    ?.map((row) => row.language_code?.toLowerCase())
    .filter((code): code is string => !!code);

  // Until the list is there — or if the request failed — everything the bundle
  // can render, rather than an empty picker nobody can get out of. The current
  // language stays in the list either way, so the Select always has its value.
  const options = (
    active ? SUPPORTED_LOCALES.filter((l) => l === selected || active.includes(l)) : SUPPORTED_LOCALES
  ).map((locale) => ({ value: locale, label: LABELS[locale] }));

  return { selected, options, change, pending: store.isPending };
}

/**
 * The picker for the public pages (login, registration, password reset). Inside
 * the application the same choice sits in the user menu, see UserMenu.
 */
export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const { selected, options, change, pending } = useLanguageSwitch();

  return (
    <Select<SupportedLocale>
      size="small"
      value={selected}
      onChange={change}
      loading={pending}
      // Re-render when the language changes underneath us.
      key={i18n.language}
      options={options}
      style={{ width: 110 }}
    />
  );
}
